<template>
  <div>
    <p
      v-if="showFilter && resetable"
      class="ml-2 mb-0 mt-0 primary--text caption"
    >
      Rajaus:
    </p>
    <div
      v-if="showFilter"
      :class="{ 'helmi-search-watch-active-filters' : resetable }"
      @click="resetForm()"
    >
      <v-row>
        <v-col class="py-0 pr-0">
          <template
            v-for="(chip, index) in chips"
          >
            <v-chip
              v-if="chip.label && chip.text"
              :key="index"
              v-bind="$attrs"
              class="ma-1"
            >
              <b class="mr-2">{{ chip.label }}:</b>
              {{ chip.text }}
            </v-chip>
            <v-chip
              v-else-if="chip.text"
              :key="index"
              v-bind="$attrs"
              class="ma-1"
            >
              <b class="mr-2">{{ chip.text }}</b>
            </v-chip>
            <v-chip
              v-else-if="chip.label === 'Myyntikohde' || chip.label === 'Vuokrakohde'"
              :key="index"
              v-bind="$attrs"
              class="ma-1"
            >
              <b class="mr-2">{{ chip.label }}</b>
            </v-chip>
          </template>
        </v-col>
        <v-icon
          v-if="resetable"
          size="18"
          class="mr-6"
          color="primary"
        >
          cancel
        </v-icon>
      </v-row>
    </div>
  </div>
</template>

<script>
import {
  additional_property_types,
  requirements,
  property_types,
  target_types,
} from '@/components/searchWatch/data';
import { currency } from '@/filters';
import { EventBus } from '@/utils';

const mapper = (item) => {
  const items = {
    area_min: 'area_min',
    area_max: 'area_max',
    price_min: 'price_min',
    price_max: 'price_max',
    rooms_min: 'rooms_min',
    rooms_max: 'rooms_max',
    requirements: 'requirements',
    property_types: 'property_types',
    target_type: 'target_type',
  };

  return items[item] || item;
};

export default {
  name: 'HelmiSearchWatchActiveFilters',

  inheritAttrs: false,

  props: {
    resetable: {
      type: Boolean,
      default: () => false,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    chips: [],
    watch: {},
  }),

  computed: {
    showFilter() {
      if (this.chips.some(c => c.label === 'Vuokrakohde')) return true;
      if (this.chips.some(c => c.label === 'Myyntikohde')) return true;
      return this.chips.some(c => c.text !== null);
    },

    area() {
      if (!this.watch.area_min ?? !this.watch.area_max) return null;
      if (+this.watch.area_min === 0 && +this.watch.area_max === 500) return null;

      return `${this.watch.area_min} - ${this.watch.area_max} m2`;
    },

    cities() {
      if (!this.watch.cities) return null;

      return this.watch.cities
        .split(',')
        .join(', ')
        .replace(/(?:,)([^,]+)$/, ' ja $1');
    },

    others() {
      if (!this.watch.requirements) return null;

      const types = this.flatten(requirements);

      return this.watch.requirements.split(',')
        .map(type => types[type])
        .join(', ')
        .replace(/(?:,)([^,]+)$/, ' ja $1');
    },

    price() {
      if (!this.watch.price_min ?? !this.watch.price_max) return null;
      if (+this.watch.price_min === 0 && +this.watch.price_max === 1000000) return null;
      if (this.watch.target_type === 'rental' && +this.watch.price_min === 0 && +this.watch.price_max === 5000) return null;

      const min = currency(this.watch.price_min, { style: 'decimal', digits: 0 });
      const max = currency(this.watch.price_max, { digits: 0 });

      return `${min} - ${max}`;
    },

    property_types() {
      if (!this.watch.property_types) return null;

      const types = this.flatten([...additional_property_types, ...property_types]);

      return this.watch.property_types.split(',')
        .map(type => types[type])
        .join(', ')
        .replace(/(?:,)([^,]+)$/, ' ja $1');
    },

    rooms() {
      if (!this.watch.rooms_min ?? !this.watch.rooms_max) return null;
      if (+this.watch.rooms_min === 0 && +this.watch.rooms_max === 10) return null;

      return `${this.watch.rooms_min} - ${this.watch.rooms_max} kpl`;
    },

    zips() {
      if (!this.watch.zips) return null;

      return this.watch.zips
        .split(',')
        .join(', ')
        .replace(/(?:,)([^,]+)$/, ' ja $1');
    },
  },

  watch: {
    filters: {
      handler(filters) {
        if (!filters) return;

        this.$set(this, 'watch', this.parseWatch(filters));
        this.$set(this, 'chips', this.createChips());
      },
      immediate: true,
    },
  },

  methods: {
    flatten(items) {
      return items
        .reduce((obj, item) => {
          obj[item.value] = item.label; // eslint-disable-line
          return obj;
        }, {});
    },

    resetForm() {
      if (this.resetable) {
        EventBus.$emit('FORM_RESET');
      }
    },

    createChips() {
      return [
        {
          label: target_types?.find(type => type.value === this.watch.target_type)?.label ?? null,
          text: this.property_types,
        },
        {
          label: 'Kaupungit',
          text: this.cities,
        },
        {
          label: 'Postinumerot',
          text: this.zips,
        },
        {
          label: 'Huoneluku',
          text: this.rooms,
        },
        {
          label: 'Pinta-ala',
          text: this.area,
        },
        {
          label: 'Hinta',
          text: this.price,
        },
        {
          label: 'Pitää olla',
          text: this.others,
        },
      ];
    },

    parseWatch() {
      return Object.entries(this.filters)
        .reduce((obj, [key, value]) => {
          if (value !== null) obj[mapper(key)] = String(value); // eslint-disable-line

          return obj;
        }, {});
    },
  },
};
</script>

<style lang="scss">
.helmi-search-watch-active-filters {
  background: rgba(0, 151, 167, 0.07) !important;
  border-radius: 4px;
  padding: 0.2em 0;
}
</style>
